/**
 * B-esper SDK Loader
 * Simplified script loading for Web Component widgets
 */

import { buildSdkUrl } from './sdkUrlHelper';
import logger from './logger';

class SDKLoader {
  constructor() {
    this.isLocalhost = this.detectLocalhost();
    this.config = this.loadConfiguration();
    this.wrapperLoaded = false;
    this.wrapperFailed = false;
  }

  /**
   * Detect if running on localhost for real-time development
   */
  detectLocalhost() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' ||
           hostname === '127.0.0.1' ||
           hostname.includes('local') ||
           hostname.includes('dev.local');
  }

  /**
   * Load configuration from enviroment
   */
  loadConfiguration() {
    // For localhost, window.localSettings is set by prestart script
    if (this.isLocalhost && window.localSettings) {
      const env = window.localSettings.env || '';
      const branch = window.localSettings.branch || '1000';
      const apiUrl = window.localSettings.apiUrl || 'http://localhost:5100';

      return {
        env,
        branch,
        isLocalhost: this.isLocalhost,
        botId: 'public_bot_external',
        apiUrl,
      };
    }

    // Cloud: use window._env_ or window.localConfig from deployment; no legacy URL fallback
    if (window._env_?.API_BASE_URL) {
      return {
        env: window._env_.ENVIRONMENT || window._env_.environment || window._env_.enviroment || '',
        branch: window._env_.BRANCH_CODE || '',
        isLocalhost: this.isLocalhost,
        botId: 'public_bot_external',
        apiUrl: window._env_.API_BASE_URL,
      };
    }
    if (window.localConfig?.apiUrl) {
      logger.debug('SDK-LOADER', 'Using window.localConfig.apiUrl');
      return {
        env: window.localConfig.enviroment || '',
        branch: window.localConfig.branchCode || '',
        isLocalhost: this.isLocalhost,
        botId: window.localConfig.botId || 'public_bot_external',
        apiUrl: window.localConfig.apiUrl,
      };
    }
    if (!this.isLocalhost) {
      logger.debug('SDK-LOADER', 'No API URL in window._env_ or localConfig; will fail until env-config.js loads');
      return {
        env: '',
        branch: '',
        isLocalhost: false,
        botId: 'public_bot_external',
        apiUrl: null,
      };
    }

    logger.debug('SDK-LOADER', 'No window config found (localhost), using local API');
    return {
      env: '',
      branch: '',
      isLocalhost: true,
      botId: 'public_bot_external',
      apiUrl: 'http://localhost:5100',
    };
  }

  /**
   * Load the Web Component widget script
   * @param {Object} options - Optional configuration (widget: false for container mode)
   */
  async loadWrapper(options = {}) {
    if (this.wrapperLoaded) return;
    if (this.wrapperFailed) throw new Error('SDK failed previously');
    
    const botId = this.config.botId || 'public_bot_external';
    
    // Determine mode from options (default to widget for backward compatibility)
    const mode = options?.widget === false ? 'container' : 'widget';
    
    let widgetUrl;
    if (this.isLocalhost) {
      widgetUrl = buildSdkUrl(this.config.apiUrl, 'bots', botId, mode);
    } else {
      let baseUrl = this.config.apiUrl;
      if (!baseUrl) {
        if (window.centralizedConfig && window.centralizedConfig.config) {
          baseUrl = window.centralizedConfig.config.API_BASE_URL || window.centralizedConfig.config.API_CUSTOMER_PORTAL_URL;
        }
        if (!baseUrl) {
          throw new Error(
            'SDK Loader: API URL is required. ' +
            'Please provide apiUrl in config or ensure env-config.js is loaded with API_BASE_URL or API_CUSTOMER_PORTAL_URL.'
          );
        }
      }
      // buildSdkUrl will handle /api prefix correctly (adds if missing, skips if present)
      widgetUrl = buildSdkUrl(baseUrl, 'bots', botId, mode);
    }
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = widgetUrl;
      script.type = 'module'; // Required for ES module syntax
      script.async = true;
      
      script.onload = () => {
        this.wrapperLoaded = true;
        resolve();
      };
      
      script.onerror = (error) => {
        this.wrapperFailed = true;
        logger.error('SDK-LOADER', 'Failed to load widget script', { error: error.message, widgetUrl });
        reject(error);
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize bot widget using Web Component
   */
  async initBot(options = {}) {
    try {
      const botId = options.botId || this.config.botId || 'public_bot_external';

      // Load the widget script if not already loaded
      if (!this.wrapperLoaded) {
        await this.loadWrapper(options);
      }
      
      // Return a mock instance for compatibility
      return {
        destroy: () => {
          // Widget destroyed
        },
        show: () => {
          // Use the global API if available
          if (window.BesperBot && window.BesperBot[botId]) {
            window.BesperBot[botId].show();
          }
        },
        hide: () => {
          // Use the global API if available
          if (window.BesperBot && window.BesperBot[botId]) {
            window.BesperBot[botId].hide();
          }
        }
      };
    } catch (error) {
      logger.error('SDK-LOADER', 'Failed to initialize bot widget', { error: error.message });
      this.wrapperFailed = true;
      return null;
    }
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Check if running in localhost mode
   */
  isLocalhostMode() {
    return this.isLocalhost;
  }

  /**
   * Reset the SDK loader state (useful for debugging)
   */
  reset() {
    this.wrapperLoaded = false;
    this.wrapperFailed = false;
  }
}

// Create singleton instance
const sdkLoader = new SDKLoader();

// Make reset method available globally for debugging
if (typeof window !== 'undefined') {
  window.resetSDK = () => {
    sdkLoader.reset();
    logger.info('SDK-LOADER', 'SDK state reset. You can now try loading the bot again.');
  };
}

export default sdkLoader;